import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Star, Timer, CheckCircle, RefreshCw, Flame } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import BottomNavigation from "@/components/BottomNavigation";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/contexts/LanguageContext";
import { useRealTimeWaits } from "@/hooks/useRealTimeWaits";
import FreshnessIndicator from "@/components/FreshnessIndicator";

import { useAuth } from "@/contexts/AuthContext";
import { useCheckinState } from "@/hooks/useCheckinState";
import { useBorderCrossings } from "@/hooks/useBorderCrossings";

import { RewardedVideoPlayer } from "@/components/RewardedVideoPlayer";
import { XPBar } from "@/components/XPBar";
import { LevelUpOverlay } from "@/components/LevelUpOverlay";
import { QuickCheckInButton } from "@/components/QuickCheckInButton";

import { useXP } from "@/hooks/useXP";
import { useStreak } from "@/hooks/useStreak";
import { celebratePoints } from "@/utils/confetti";
import { useFeaturedAds } from "@/hooks/useFeaturedAds";
import { FeaturedCrossingAd } from "@/components/FeaturedCrossingAd";
import { SponsoredTipCard } from "@/components/SponsoredTipCard";

const Home = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useLanguage();
  const { user } = useAuth();
  const { waitTimes, loading, lastUpdated, refreshWaitTimes, getWaitTime, getWaitTimeColor } = useRealTimeWaits();
  const { currentCheckin, completeCheckout } = useCheckinState();
  const { crossings: borderCrossingsData } = useBorderCrossings();
  const { addXP } = useXP();
  const { currentStreak, updateStreak } = useStreak();
  
  const [favoriteCrossing, setFavoriteCrossing] = useState<string | null>(null);
  const [loadingFavorite, setLoadingFavorite] = useState(true);
  const { featuredAds, sponsoredTips, dismissAd } = useFeaturedAds(favoriteCrossing || undefined);
  const [showAllCrossings, setShowAllCrossings] = useState(false);
  const [waitTime, setWaitTime] = useState<string>("0 min");
  const [showCheckoutAd, setShowCheckoutAd] = useState(false);
  
  // Load favorite crossing
  useEffect(() => {
    const loadFavorite = async () => {
      if (!user) {
        setLoadingFavorite(false);
        return;
      }
      
      setLoadingFavorite(true);
      
      try {
        const { data } = await supabase
          .from('users')
          .select('favorite_crossing_id, border_crossings(name)')
          .eq('id', user.id)
          .single();
        
        if (data?.border_crossings) {
          setFavoriteCrossing((data.border_crossings as any).name);
        }
      } catch (error) {
        console.error('Error loading favorite crossing:', error);
      } finally {
        setLoadingFavorite(false);
      }
    };
    
    loadFavorite();
  }, [user]);
  
  // Update wait time timer for current check-in
  useEffect(() => {
    if (currentCheckin) {
      const checkInTime = new Date(currentCheckin.created_at);
      const interval = setInterval(() => {
        const now = new Date();
        const diffMs = now.getTime() - checkInTime.getTime();
        const diffMin = Math.floor(diffMs / (1000 * 60));
        setWaitTime(`${diffMin} min`);
      }, 1000);

      return () => clearInterval(interval);
    } else {
      setWaitTime("0 min");
    }
  }, [currentCheckin]);

  // Listen for checkout ad trigger
  useEffect(() => {
    const handleShowAd = () => {
      setShowCheckoutAd(true);
    };

    window.addEventListener('show-checkout-ad', handleShowAd);
    return () => window.removeEventListener('show-checkout-ad', handleShowAd);
  }, []);

  const handleCheckOut = async () => {
    if (!currentCheckin) return;
    
    const confirmed = window.confirm(t('confirmCheckout') || "Are you sure you want to check out?");
    if (!confirmed) return;
    
    try {
      // Show rewarded ad opportunity (true flag)
      const waitTimeMinutes = await completeCheckout(true);
      
      // Award 25 XP for check-out
      await addXP(25);
      await updateStreak();
      
      celebratePoints(25);
      
      toast({
        title: "🎯 " + (t('checkedOut') || 'Checked Out!'),
        description: `${t('waitTime') || 'Wait time'}: ${waitTimeMinutes} ${t('minutes') || 'minutes'}. +25 XP!`,
      });
    } catch (error) {
      console.error('Checkout error:', error);
      toast({
        title: t('error') || "Error",
        description: t('checkoutFailed') || "Checkout failed. Please try again",
        variant: "destructive"
      });
    }
  };
  
  const handleToggleFavorite = async (crossingName: string) => {
    if (!user) {
      toast({
        title: t('signInRequired') || "Sign in required",
        description: t('authRequired') || "Please sign in to save favorites",
      });
      navigate('/auth');
      return;
    }
    
    try {
      const isFavorite = favoriteCrossing === crossingName;
      
      const { data: crossingData } = await supabase
        .from('border_crossings')
        .select('id')
        .eq('name', crossingName)
        .single();
      
      if (!crossingData) return;
      
      await supabase
        .from('users')
        .update({
          favorite_crossing_id: isFavorite ? null : crossingData.id
        })
        .eq('id', user.id);
      
      setFavoriteCrossing(isFavorite ? null : crossingName);
      
      toast({
        title: isFavorite ? (t('favoriteRemoved') || "Favorite removed") : "⭐ " + (t('favoriteAdded') || "Favorite added!"),
        description: isFavorite 
          ? t('crossingRemovedFromFavorites') || "Crossing removed from favorites"
          : `${crossingName} ${t('isNowFavorite') || 'is now your favorite crossing'}`,
      });
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  // Use actual border crossings from database
  const borderCrossings = borderCrossingsData.map(c => c.display_name);
  
  const orderedCrossings = [...borderCrossingsData].sort((a, b) => {
    if (a.display_name === favoriteCrossing) return -1;
    if (b.display_name === favoriteCrossing) return 1;
    return a.order_index - b.order_index;
  });
  
  const visibleCrossings = !showAllCrossings && favoriteCrossing
    ? orderedCrossings.filter(c => c.display_name === favoriteCrossing)
    : orderedCrossings;

  // Helper function to inject ads between crossings
  const renderCrossingsWithAds = () => {
    const items = [];
    let adIndex = 0;
    let tipIndex = 0;

    visibleCrossings.forEach((crossing, i) => {
      items.push({ type: 'crossing', data: crossing, key: crossing.display_name });

      // Inject featured ad after favorite or every 5 crossings
      if ((i === 0 && favoriteCrossing) || (i + 1) % 5 === 0) {
        if (featuredAds[adIndex]) {
          items.push({ 
            type: 'featured_ad', 
            data: featuredAds[adIndex], 
            key: `ad-${featuredAds[adIndex].id}` 
          });
          adIndex++;
        }
      }

      // Inject sponsored tip after 2nd crossing
      if (i === 1 && sponsoredTips[tipIndex]) {
        items.push({ 
          type: 'sponsored_tip', 
          data: sponsoredTips[tipIndex], 
          key: `tip-${sponsoredTips[tipIndex].id}` 
        });
        tipIndex++;
      }
    });

    return items;
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="p-4 space-y-4">
        {/* User Stats: XP Bar or Check-in Status */}
        {user && !currentCheckin && (
          <div className="flex items-center justify-between px-2">
            <XPBar />
            {currentStreak > 0 && (
              <Badge variant="secondary" className="flex items-center gap-1">
                <Flame className="w-4 h-4 text-orange-500" />
                {currentStreak} {t('dayStreak') || 'day streak'}
              </Badge>
            )}
          </div>
        )}
        
        {/* Check-in Status in Header Style */}
        {user && currentCheckin && (
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Timer className="w-4 h-4 text-primary animate-pulse" />
                  <div>
                    <p className="text-xs text-muted-foreground">{t('checkedInAt') || 'Checked in at'}</p>
                    <p className="font-semibold text-sm">{currentCheckin.garita}</p>
                  </div>
                </div>
                <Badge variant="secondary" className="text-base font-bold">
                  {waitTime}
                </Badge>
              </div>
              <Button 
                onClick={handleCheckOut} 
                className="w-full mt-2"
                size="sm"
                variant="default"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                {t('checkout') || 'Check Out'}
              </Button>
            </CardContent>
          </Card>
        )}
        
        {/* Quick Check-In Button */}
        {!currentCheckin && <QuickCheckInButton />}
        
        {/* Favorite Crossing Card - Loading Skeleton */}
        {loadingFavorite && (
          <Card className="border-2 border-primary/30 bg-gradient-to-br from-primary/10 to-secondary/10 shadow-lg animate-pulse">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 bg-muted rounded-full" />
                  <div className="h-5 w-40 bg-muted rounded" />
                </div>
              </div>
              <div className="text-center py-6 space-y-4">
                <div className="h-10 w-48 bg-muted rounded mx-auto" />
                <div className="h-16 w-32 bg-muted rounded mx-auto" />
                <div className="h-4 w-56 bg-muted rounded mx-auto" />
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Favorite Crossing Card - HERO ELEMENT */}
        {!loadingFavorite && favoriteCrossing && (
          <Card className="border-2 border-primary/30 bg-gradient-to-br from-primary/10 to-secondary/10 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Star className="w-6 h-6 fill-yellow-500 text-yellow-500" />
                  <h3 className="text-lg font-semibold">{t('favoriteCrossing') || 'Favorite Crossing'}</h3>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowAllCrossings(!showAllCrossings)}
                >
                  {showAllCrossings ? (t('showLess') || 'Show Less') : (t('viewAll') || 'View All')}
                </Button>
              </div>
              <div className="text-center py-6">
                <h2 className="text-4xl font-bold text-primary mb-4">{favoriteCrossing}</h2>
                <p className={`text-6xl font-black ${getWaitTimeColor(getWaitTime(favoriteCrossing, 'vehicle', 'Normal'))} mb-2`}>
                  {getWaitTime(favoriteCrossing, 'vehicle', 'Normal') || '--'} <span className="text-3xl">{t('min') || 'min'}</span>
                </p>
                <div className="mt-3 flex justify-center">
                  <FreshnessIndicator timestamp={lastUpdated} prefix />
                </div>

              </div>
            </CardContent>
          </Card>
        )}
        
        {/* All Crossings */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-lg">{t('borderCrossings') || 'Border Crossings'}</h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={refreshWaitTimes}
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
          
          {/* Loading Skeletons */}
          {loading && (
            <>
              {[1, 2, 3, 4, 5].map((i) => (
                <Card key={`skeleton-${i}`} className="animate-pulse">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="h-5 w-48 bg-muted rounded" />
                      <div className="w-4 h-4 bg-muted rounded-full ml-auto" />
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="h-4 w-20 bg-muted rounded" />
                        <div className="h-6 w-16 bg-muted rounded" />
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="h-4 w-24 bg-muted rounded" />
                        <div className="h-6 w-16 bg-muted rounded" />
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="h-4 w-28 bg-muted rounded" />
                        <div className="h-6 w-16 bg-muted rounded" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </>
          )}
          
          {/* Actual Crossing Cards */}
          {!loading && renderCrossingsWithAds().map((item) => {
            if (item.type === 'featured_ad') {
              return (
                <FeaturedCrossingAd
                  key={item.key}
                  ad={item.data}
                  onDismiss={() => dismissAd(item.data.id)}
                />
              );
            }

            if (item.type === 'sponsored_tip') {
              return (
                <SponsoredTipCard
                  key={item.key}
                  ad={item.data}
                  onDismiss={() => dismissAd(item.data.id)}
                />
              );
            }

            // Regular crossing card
            const crossing = item.data;
            
            // Format lane types for display
            const formatLaneType = (lane: string) => {
              if (lane === 'standard') return t('normal') || 'Normal';
              if (lane === 'sentri') return 'SENTRI';
              if (lane === 'ready_lane') return 'Ready Lane';
              return lane;
            };
            
            // Group lanes by mode
            const vehicleLanes = crossing.lane_types.filter(
              (lane: string) => lane !== 'pedestrian' && crossing.modes.includes('driving')
            );
            const hasPedestrian = crossing.modes.includes('walking');
            
            return (
              <Card key={item.key} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <h4 className="font-bold text-base">{crossing.display_name}</h4>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleToggleFavorite(crossing.display_name)}
                      className="h-6 w-6 p-0"
                    >
                      <Star
                        className={`w-4 h-4 ${
                          crossing.display_name === favoriteCrossing
                            ? 'fill-yellow-500 text-yellow-500'
                            : 'text-muted-foreground'
                        }`}
                      />
                    </Button>
                  </div>
                  
                  <div className="space-y-2">
                    {hasPedestrian && (
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-sm">{t('pedestrian') || 'Pedestrian'}</span>
                        <span className={`text-xl font-bold ${getWaitTimeColor(
                          getWaitTime(crossing.name, 'walking', 'Pedestrian')
                        )}`}>
                          {getWaitTime(crossing.name, 'walking', 'Pedestrian') || '--'}
                        </span>
                      </div>
                    )}
                    
                    {vehicleLanes.length > 0 && (
                      <div>
                        <p className="font-bold text-sm mb-1">{t('vehicle') || 'Vehicle'}</p>
                        <div className="space-y-1 pl-2">
                          {vehicleLanes.map((lane: string) => (
                            <div key={lane} className="flex items-center justify-between">
                              <span className="text-sm">{formatLaneType(lane)}</span>
                              <span className={`text-xl font-bold ${getWaitTimeColor(
                                getWaitTime(crossing.name, 'vehicle', formatLaneType(lane))
                              )}`}>
                                {getWaitTime(crossing.name, 'vehicle', formatLaneType(lane)) || '--'}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
      
      {/* Level Up Overlay */}
      <LevelUpOverlay />
      
      {/* Checkout Rewarded Ad */}
      <RewardedVideoPlayer 
        open={showCheckoutAd}
        onClose={() => setShowCheckoutAd(false)}
      />
      
      {/* Bottom Navigation */}
      <BottomNavigation />
    </div>
  );
};

export default Home;
